import { invariant } from '@epic-web/invariant';
import { Box } from '@talos/kyoko';
import { useCallback, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  getOrderDetailsRouteFn,
  isCurrentRouteCustomerOrderDetails,
  matchMonitoringSpacePathname,
  matchTradingPathname,
} from '../../../../containers/Routes/routes';
import { OrderDetails } from '../../../../containers/Trading/Markets/OrderDetails/OrderDetails';
import type { OrderDetailsPath } from '../../../../containers/Trading/Markets/OrderDetails/types';
import { useFeatureFlag } from '../../../../hooks';
import { useAppStateSelector } from '../../../../providers/AppStateProvider';
import type { OrderDetailsPanelConfig } from '../../../../types/LayoutConfig';
import { Panel } from '../../../Layout/Components/Panel';
import { selectCurrentLayout, selectLayoutSection } from '../../AppLayoutSlice';
import { DEFAULT_LAYOUT_ID } from '../../Layouts/tokens';
import type { LayoutPanelProps } from '../../types';

export function OrderDetailsPanel({ api, params }: LayoutPanelProps<OrderDetailsPanelConfig>) {
  const navigate = useNavigate();
  const location = useLocation();
  const layoutSection = useAppStateSelector(selectLayoutSection);
  const currentLayout = useAppStateSelector(selectCurrentLayout);
  const { enableMonitoringSpaces } = useFeatureFlag();
  const getOrderDetailsRoute = getOrderDetailsRouteFn(
    layoutSection,
    layoutSection === 'trading' ? true : layoutSection === 'monitoring' ? enableMonitoringSpaces : false
  );

  const isOrderIDCustomerOrder = isCurrentRouteCustomerOrderDetails(location.pathname);
  const customerOrderID = isOrderIDCustomerOrder ? params.orderID : undefined;
  const orderID = isOrderIDCustomerOrder ? undefined : params.orderID;

  const handleSelectTab = useCallback(
    (openTab: OrderDetailsPath | undefined) => {
      api.updateParameters({ openTab, orderID: params.orderID });
      const routeMatch =
        layoutSection === 'trading'
          ? matchTradingPathname(location.pathname, ':type/:orderID/:tab?')
          : layoutSection === 'monitoring' && enableMonitoringSpaces
            ? matchMonitoringSpacePathname(location.pathname, ':type/:orderID/:tab?')
            : matchPath({ path: getOrderDetailsRoute() }, location.pathname);
      invariant(
        routeMatch?.params?.type == null ||
          routeMatch?.params?.type === 'customer-order' ||
          routeMatch?.params?.type === 'order',
        'Invalid order details type'
      );
      navigate(
        getOrderDetailsRoute({
          orderID: params.orderID,
          tab: openTab,
          type: routeMatch?.params?.type ?? 'order',
          layoutId: currentLayout?.id,
          navGroupName: routeMatch?.params?.navGroupName,
        })
      );
    },
    [
      api,
      params.orderID,
      location.pathname,
      navigate,
      getOrderDetailsRoute,
      currentLayout?.id,
      layoutSection,
      enableMonitoringSpaces,
    ]
  );

  const handleClose = useCallback(() => {
    // TODO add mixpanel tracking
    api.close();
  }, [api]);

  const widthRef = useRef<HTMLDivElement>(null);

  return (
    <Panel panelApi={api} data-testid="order-details-panel">
      <Box h="100%" ref={widthRef}>
        <OrderDetails
          widthRef={widthRef}
          orderID={orderID}
          customerOrderID={customerOrderID}
          selectedTab={params.openTab}
          onSelectTab={handleSelectTab}
          onClose={handleClose}
          useLink={false}
          layoutId={currentLayout?.id ?? DEFAULT_LAYOUT_ID}
        />
      </Box>
    </Panel>
  );
}
